/**
 * FauxDS Combobox - Recreated from Figma
 *
 * ⚠️ This is a SIMPLIFIED RECREATION for prototyping. It is NOT the real Slate Combobox.
 *
 * Figma design: https://www.figma.com/design/YoQphMxdNGGmcBz9M9IpNe/-GK--Combobox?node-id=65540-527
 * Multiselect: https://www.figma.com/design/YoQphMxdNGGmcBz9M9IpNe/-GK--Combobox?node-id=65835-931
 *
 * Features:
 * - Type-to-filter options (single and multiselect)
 * - Configurable open/close animation (none, fade, slide)
 * - Configurable animation duration
 * - Multiselect mode: search input filters listbox; selected values shown as dismissible Tags; search clears on blur
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, {
  forwardRef,
  useId,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { createPortal } from "react-dom";

import Tag from "./Tag";

export type ComboboxOpenAnimation = "none" | "fade" | "slide";

export interface ComboboxOption {
  label: string;
  value: string;
  disabled?: boolean;
  /** When true, the selection checkmark (tag) is hidden for this option even when selected */
  hiddenTag?: boolean;
}

export interface ComboboxProps {
  /** Label text */
  label?: string;
  /** Placeholder when empty */
  placeholder?: string;
  /** Options list */
  options?: ComboboxOption[];
  /** Current value (controlled, single-select) */
  value?: string;
  /** Default value (uncontrolled, single-select) */
  defaultValue?: string;
  /** Current values (controlled, multiselect only) */
  values?: string[];
  /** Default values (uncontrolled, multiselect only) */
  defaultValues?: string[];
  /** Change handler; receives string in single-select, string[] in multiselect */
  onChange?: (value: string | string[]) => void;
  /** Allow multiple selections; selected values shown as dismissible Tags */
  multiselect?: boolean;
  /** Open animation type */
  openAnimation?: ComboboxOpenAnimation;
  /** Open animation duration in ms */
  openDuration?: number;
  /** Close animation duration in ms */
  closeDuration?: number;
  /** Disabled state */
  disabled?: boolean;
  /** Helper text below input */
  helper?: string;
  /** Error state */
  error?: boolean | string;
  /** Show "Optional" tag next to the label */
  optional?: boolean;
  /** Optional class name */
  className?: string;
  /** aria-label when no label */
  "aria-label"?: string;
  /**
   * Optional "Add" action at the top of the menu.
   * When set, shows "+ Add [addNewLabel]" (e.g. addNewLabel="Country" → "+ Add Country").
   */
  addNewLabel?: string;
  /** Called when the "+ Add [option]" button is clicked with the current input text. Requires addNewLabel. */
  onAddNew?: (enteredText: string) => void;
}

const ChevronDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M4 10L8 6L12 10"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M8 3v10M3 8h10"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M3 8l3 3 7-7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(
  (
    {
      label,
      placeholder = "Search or select…",
      options = [],
      value,
      defaultValue = "",
      values: controlledValues,
      defaultValues = [],
      onChange,
      multiselect = false,
      openAnimation = "fade",
      openDuration = 150,
      closeDuration = 100,
      disabled = false,
      helper,
      error,
      optional = false,
      className = "",
      "aria-label": ariaLabel,
      addNewLabel,
      onAddNew,
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = `combobox-${generatedId}`;
    const listboxId = `combobox-listbox-${generatedId}`;
    const helperId = `combobox-helper-${generatedId}`;

    const [isOpen, setIsOpen] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [internalValues, setInternalValues] =
      useState<string[]>(defaultValues);
    const [inputText, setInputText] = useState("");
    const [isClosing, setIsClosing] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputWrapperRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const filterInputRef = useRef<HTMLInputElement>(null);

    const currentValue = value !== undefined ? value : internalValue;
    const currentValues =
      controlledValues !== undefined ? controlledValues : internalValues;
    const selectedOption = options.find((o) => o.value === currentValue);
    const singleSelectedOption =
      multiselect && currentValues.length === 1
        ? options.find((o) => o.value === currentValues[0])
        : null;
    // When one selection (multiselect or single-select), show its label; otherwise show what user is typing
    const displayValue = singleSelectedOption
      ? singleSelectedOption.label
      : !isOpen && selectedOption
        ? selectedOption.label
        : inputText;

    const hasError = !!error;
    const errorMessage = typeof error === "string" ? error : null;

    // Filter options by input text (case-insensitive)
    const filteredOptions = useMemo(() => {
      if (!inputText.trim()) return options;
      const q = inputText.trim().toLowerCase();
      return options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
      );
    }, [options, inputText]);

    // Keep list mounted while closing so close animation can run
    const showList = isOpen;

    // Sync input text when value is set externally (single-select); multiselect closed = empty input
    useEffect(() => {
      if (!isOpen) {
        if (multiselect) {
          setInputText("");
        } else {
          const opt = options.find((o) => o.value === currentValue);
          if (opt) setInputText(opt.label);
        }
      }
    }, [multiselect, currentValue, currentValues, options, isOpen]);

    // Position dropdown 4px below the input (not the whole field with label/helper)
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
    useEffect(() => {
      if (isOpen && inputWrapperRef.current) {
        const rect = inputWrapperRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    }, [isOpen, displayValue]);

    // Close when clicking outside the input or the dropdown list
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        const hitContainer = containerRef.current?.contains(target);
        const hitList = listRef.current?.contains(target);
        if (!hitContainer && !hitList) {
          handleClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const handleClose = () => {
      if (!isOpen) return;
      setIsClosing(true);
      const duration = closeDuration;
      const t = setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
        if (multiselect) setInputText("");
      }, duration);
      return () => clearTimeout(t);
    };

    const handleOpen = () => {
      if (disabled) return;
      setIsClosing(false);
      setIsOpen(true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setInputText(v);
      if (!isOpen) setIsOpen(true);
      if (
        !multiselect &&
        !options.some((o) => o.value === currentValue && o.label === v)
      ) {
        if (value === undefined) setInternalValue("");
        onChange?.("");
      }
    };

    const handleRemoveValue = (valueToRemove: string) => {
      const next = currentValues.filter((v) => v !== valueToRemove);
      if (controlledValues === undefined) setInternalValues(next);
      onChange?.(next);
    };

    const handleSelect = (option: ComboboxOption) => {
      if (multiselect) {
        const isSelected = currentValues.includes(option.value);
        const next = isSelected
          ? currentValues.filter((v) => v !== option.value)
          : [...currentValues, option.value];
        if (controlledValues === undefined) setInternalValues(next);
        onChange?.(next);
        return;
      }
      if (value === undefined) {
        setInternalValue(option.value);
        setInputText(option.label);
      }
      onChange?.(option.value);
      handleClose();
    };

    const handleInputFocus = () => {
      if (
        !multiselect &&
        selectedOption &&
        inputText !== selectedOption.label
      ) {
        setInputText(selectedOption.label);
      }
      handleOpen();
    };

    const handleFilterInputFocus = () => handleOpen();

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        handleClose();
        (e.target as HTMLInputElement).blur();
      }
      if (e.key === "ArrowDown" && !showList && filteredOptions.length) {
        e.preventDefault();
        handleOpen();
      }
    };

    const getInputStyles = () => {
      if (disabled) {
        return {
          bg: "bg-[var(--slate-color-action-surface-disabled)]",
          border: "border-[var(--slate-color-action-border-disabled)]",
          text: "text-[var(--slate-color-action-text-disabled)]",
          placeholder:
            "placeholder:text-[var(--slate-color-action-text-disabled)]",
        };
      }
      if (hasError) {
        return {
          bg: "bg-fs-surface-primary",
          border: "border-[var(--slate-color-action-border-critical)]",
          text: "text-[var(--slate-color-action-text-default)]",
          placeholder:
            "placeholder:text-[var(--slate-color-action-text-placeholder)]",
        };
      }
      if (isOpen) {
        return {
          bg: "bg-fs-surface-primary",
          border: "border-[var(--slate-color-action-border-active)]",
          text: "text-[var(--slate-color-action-text-default)]",
          placeholder:
            "placeholder:text-[var(--slate-color-action-text-placeholder)]",
        };
      }
      return {
        bg: "bg-fs-surface-primary",
        border: "border-[var(--slate-color-action-border-subdued)]",
        text: "text-[var(--slate-color-action-text-default)]",
        placeholder:
          "placeholder:text-[var(--slate-color-action-text-placeholder)]",
      };
    };

    const inputStyles = getInputStyles();

    const getListAnimationClass = () => {
      if (openAnimation === "none") return "";
      if (openAnimation === "fade") {
        const duration = isClosing ? closeDuration : openDuration;
        return isClosing
          ? "animate-combobox-fade-out"
          : "animate-combobox-fade-in";
      }
      if (openAnimation === "slide") {
        return isClosing
          ? "animate-combobox-slide-out"
          : "animate-combobox-slide-in";
      }
      return "";
    };

    return (
      <div ref={containerRef} className={cn("flex flex-col", className)}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "block text-sm",
              disabled
                ? "text-[var(--slate-color-action-text-disabled)]"
                : hasError
                  ? "text-[var(--slate-color-action-text-critical)]"
                  : "text-[var(--slate-color-action-text-default)]"
            )}
          >
            {label}
            {optional && (
              <span className="ml-1 text-[var(--slate-color-action-text-placeholder)]">
                (optional)
              </span>
            )}
          </label>
        )}

        <div ref={inputWrapperRef} className="relative w-full">
          {multiselect ? (
            <div
              onClick={() => filterInputRef.current?.focus()}
              className={cn(
                "relative w-full rounded border py-2 pr-10 transition-colors duration-150",
                isOpen
                  ? "min-h-10 pl-4"
                  : "flex h-10 flex-nowrap items-center gap-2 overflow-hidden pl-2",
                inputStyles.bg,
                inputStyles.border,
                inputStyles.text,
                inputStyles.placeholder,
                disabled ? "cursor-not-allowed" : "cursor-text",
                "focus-within:border-[var(--slate-color-action-border-active)] focus-within:outline focus-within:outline-1 focus-within:-outline-offset-1 focus-within:outline-[var(--slate-color-action-border-active)]"
              )}
            >
              {!isOpen && (
                <div className="flex w-max shrink-0 flex-nowrap items-center gap-2 overflow-visible">
                  {currentValues.map((val) => {
                    const opt = options.find((o) => o.value === val);
                    return (
                      <Tag
                        key={val}
                        label={opt?.label ?? val}
                        onDismiss={() => handleRemoveValue(val)}
                        className="shrink-0"
                      />
                    );
                  })}
                </div>
              )}
              {!isOpen && (
                <div
                  className="pointer-events-none absolute inset-y-0 right-0 w-10"
                  style={{
                    borderRadius: "0 3px 3px 0",
                    background:
                      "linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 23.08%)",
                  }}
                  aria-hidden
                />
              )}
              <input
                ref={(node) => {
                  (
                    filterInputRef as React.MutableRefObject<HTMLInputElement | null>
                  ).current = node;
                  if (typeof ref === "function") ref(node);
                  else if (ref != null)
                    (
                      ref as React.MutableRefObject<HTMLInputElement | null>
                    ).current = node;
                }}
                id={inputId}
                type="text"
                role="combobox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-label={ariaLabel || label || "Combobox"}
                aria-describedby={helper || errorMessage ? helperId : undefined}
                aria-invalid={hasError}
                aria-multiselectable={true}
                value={inputText}
                onChange={handleInputChange}
                onFocus={handleFilterInputFocus}
                onKeyDown={handleInputKeyDown}
                placeholder={currentValues.length === 0 ? placeholder : ""}
                disabled={disabled}
                className={cn(
                  "block w-full min-w-0 border-0 bg-transparent p-0 text-base text-[var(--slate-color-action-text-default)] placeholder:text-[var(--slate-color-action-text-placeholder)] focus:outline-hidden",
                  disabled ? "cursor-not-allowed" : ""
                )}
              />
            </div>
          ) : (
            <input
              ref={ref}
              id={inputId}
              type="text"
              role="combobox"
              aria-expanded={isOpen}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-label={ariaLabel || label || "Combobox"}
              aria-describedby={helper || errorMessage ? helperId : undefined}
              aria-invalid={hasError}
              value={displayValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={handleInputKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                "h-10 w-full rounded border px-4 pr-10 text-left text-base transition-colors duration-150",
                inputStyles.bg,
                inputStyles.border,
                inputStyles.text,
                inputStyles.placeholder,
                disabled ? "cursor-not-allowed" : "",
                "focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-[var(--slate-color-action-border-active)]"
              )}
            />
          )}
          <div
            className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-[var(--slate-color-action-icon-default)]"
            aria-hidden
          >
            {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </div>
        </div>

        {(helper || errorMessage) && (
          <span
            id={helperId}
            className={cn(
              "mt-1 block text-xs",
              hasError
                ? "text-[var(--slate-color-action-text-critical)]"
                : "text-[var(--slate-color-action-text-default)]"
            )}
          >
            {errorMessage || helper}
          </span>
        )}

        {showList &&
          createPortal(
            <div
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-multiselectable={multiselect}
              className={cn(
                "bg-fs-surface-primary fixed z-[10000] max-h-60 overflow-auto rounded-sm border border-[var(--slate-color-action-border-subdued)] shadow-lg",
                getListAnimationClass()
              )}
              style={{
                top: position.top,
                left: position.left,
                width: position.width,
                ["--combobox-duration" as string]: `${isClosing ? closeDuration : openDuration}ms`,
              }}
            >
              {addNewLabel != null && onAddNew != null && (
                <button
                  type="button"
                  onClick={() => {
                    onAddNew(inputText.trim());
                    handleClose();
                  }}
                  className="flex w-full items-center gap-2 border-y border-[var(--slate-color-action-border-subdued)] px-4 py-2 text-left text-sm text-[var(--slate-color-action-text-placeholder)] hover:bg-[var(--slate-color-action-surface-inverse-hover)] hover:text-[var(--slate-color-action-text-default)] focus:bg-[var(--slate-color-action-surface-inverse-hover)] focus:text-[var(--slate-color-action-text-default)] focus:outline-hidden"
                >
                  <span
                    className="shrink-0 text-[var(--slate-color-action-text-placeholder)]"
                    aria-hidden
                  >
                    <PlusIcon />
                  </span>
                  Add {inputText.trim() ? inputText.trim() : addNewLabel}
                </button>
              )}
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-2 text-sm text-[var(--slate-color-action-text-placeholder)]">
                  No results
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = multiselect
                    ? currentValues.includes(option.value)
                    : option.value === currentValue;
                  const showCheckmark =
                    multiselect && isSelected && option.hiddenTag !== true;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={option.disabled}
                      onClick={() => handleSelect(option)}
                      className={cn(
                        "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm text-[var(--slate-color-action-text-default)]",
                        option.disabled
                          ? "cursor-not-allowed opacity-50"
                          : "cursor-pointer hover:bg-[var(--slate-color-action-surface-inverse-hover)]",
                        "focus:bg-[var(--slate-color-action-surface-inverse-hover)] focus:outline-hidden"
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {option.label}
                      </span>
                      {multiselect && showCheckmark && (
                        <span
                          className="shrink-0 text-[var(--slate-color-action-icon-default)]"
                          aria-hidden
                        >
                          <CheckIcon />
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>,
            document.body
          )}
      </div>
    );
  }
);

Combobox.displayName = "Combobox";

export default Combobox;
